/**
 * FormCard - A reusable component for form sections with a themed header and content area
 */
import { Stack, styled, Typography } from "@mui/material";
import React from "react";

import { Box } from "@/Components/v2/atoms/Box";

interface FormCardProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

/**
 * FormCard - A reusable component for form sections with a themed header and content area
 *
 * @param title - The main heading text
 * @param subtitle - The subheading text
 * @param children - Content to display in the content area
 */
const FormCard: React.FC<FormCardProps> = ({ title, subtitle, children }) => {
  return (
    <FormCardContainer>
      {/* Header section */}
      <FormCardHeader>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="h2" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body1">{subtitle}</Typography>
        </Stack>
      </FormCardHeader>

      {/* Content section */}
      <FormCardContent>{children}</FormCardContent>
    </FormCardContainer>
  );
};

// Styled components
const FormCardContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FormCardHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.formCard.headerBackground,
  border: `1px solid ${theme.palette.formCard.border}`,
  borderRadius: `8px 8px 0 0`,
  padding: theme.spacing(3),
}));

const FormCardContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.formCard.contentBackground,
  borderRadius: `0 0 8px 8px`,
  border: `1px solid ${theme.palette.formCard.border}`,
  borderTop: "none",
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
}));

export default FormCard;
